import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/** Accepts the project URL with or without a trailing /rest/v1 or slash, as copied from the dashboard. */
export function normalizeSupabaseUrl(raw: string | undefined): string | undefined {
  const v = raw?.trim();
  if (!v) return undefined;
  return v.replace(/\/+$/, '').replace(/\/(rest|auth)\/v1$/i, '').replace(/\/+$/, '');
}

const url = normalizeSupabaseUrl(import.meta.env.VITE_SUPABASE_URL as string | undefined);
const key = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined)?.trim() || undefined;

/** Which settings this build received (names only, never values), for the setup notice. */
export const supabaseSettings = {
  url: Boolean(url && !url.includes('YOUR-PROJECT-REF')),
  key: Boolean(key && key !== 'sb_publishable_xxx'),
};

/** False until the Supabase URL and publishable key are set in the environment. */
export const supabaseConfigured = supabaseSettings.url && supabaseSettings.key;

// The publishable key is safe in the browser: row-level security decides what each user can read or write.
export const supabase: SupabaseClient = createClient(url ?? 'http://localhost:54321', key ?? 'missing-key', {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: 'pkce' },
});

/** Turns Supabase/Postgres errors into messages a learner can act on. */
export function friendlyError(err: unknown): string {
  const e = err as { message?: string; code?: string } | null;
  const msg = e?.message ?? '';
  if (/not enrolled/i.test(msg)) return 'You need to be enrolled in this course to open this lesson.';
  if (/lesson locked/i.test(msg)) return 'Finish the previous lesson first; this one unlocks after it.';
  if (/completion requirements not met/i.test(msg)) return 'This lesson isn’t complete yet. Watch the video or finish the activity first.';
  if (/invalid login credentials/i.test(msg)) return 'That email and password don’t match an account.';
  if (/email not confirmed/i.test(msg)) return 'Please confirm your email first. Check your inbox for the verification link.';
  if (/user already registered/i.test(msg)) return 'An account with that email already exists. Try signing in or resetting your password.';
  if (/failed to fetch|network/i.test(msg)) return 'We couldn’t reach the server. Check your connection and try again.';
  return msg || 'Something went wrong. Please try again.';
}
